import { ORB_FLASH_BEEP_EVENT, ORB_FLASH_BEEP_FREQUENCY, ORB_FLASH_BEEP_GAIN, ORB_FLASH_BEEP_WAVEFORM, ORB_FLASH_FEEDBACK_DURATION_MS, ORB_MOVE_TONE_FREQUENCY, ORB_MOVE_TONE_GAIN, ORB_MOVE_TONE_WAVEFORM, } from '../interaction/SelectionFeedbackConfig';
/**
 * Lightweight audio routing helper.  It wraps the Web Audio API so we can
 * declaratively register cues and trigger them from gameplay systems without
 * sprinkling oscillator logic everywhere.
 */
export class AudioBus {
    constructor() {
        this.context = null;
        this.gain = null;
        this.sustainNodes = new Map();
        this.registry = new Map();
        // Default cues
        this.registerSustain('orbHold', {
            frequency: ORB_MOVE_TONE_FREQUENCY,
            type: ORB_MOVE_TONE_WAVEFORM,
            gain: ORB_MOVE_TONE_GAIN,
        });
        this.registerOneShot(ORB_FLASH_BEEP_EVENT, {
            type: 'one-shot',
            frequency: ORB_FLASH_BEEP_FREQUENCY,
            duration: ORB_FLASH_FEEDBACK_DURATION_MS / 1000,
            gain: ORB_FLASH_BEEP_GAIN,
            waveform: ORB_FLASH_BEEP_WAVEFORM,
        });
    }
    registerOneShot(name, config) {
        this.registry.set(name, config);
    }
    registerSustain(name, config) {
        this.registry.set(name, { type: 'sustain', config });
    }
    trigger(name) {
        var _a, _b, _c, _d;
        const event = this.registry.get(name);
        if (!event)
            return;
        this.ensureContext();
        if (!this.context || !this.gain)
            return;
        if (event.type === 'one-shot') {
            const osc = this.context.createOscillator();
            const gainNode = this.context.createGain();
            osc.type = (_a = event.waveform) !== null && _a !== void 0 ? _a : 'triangle';
            osc.frequency.value = (_b = event.frequency) !== null && _b !== void 0 ? _b : 440;
            gainNode.gain.value = (_c = event.gain) !== null && _c !== void 0 ? _c : 0.08;
            osc.connect(gainNode).connect(this.gain);
            osc.start();
            osc.stop(this.context.currentTime + ((_d = event.duration) !== null && _d !== void 0 ? _d : 0.25));
            return;
        }
        // Sustain events toggle instead of triggering repeatedly
        const key = name;
        if (this.sustainNodes.has(key)) {
            this.stopSustain(name);
        }
        else {
            this.startSustain(name);
        }
    }
    startSustain(name) {
        var _a, _b;
        const event = this.registry.get(name);
        if (!event || event.type !== 'sustain')
            return;
        this.ensureContext();
        if (!this.context || !this.gain)
            return;
        if (this.sustainNodes.has(name))
            return;
        const oscillator = this.context.createOscillator();
        oscillator.type = (_a = event.config.type) !== null && _a !== void 0 ? _a : 'sine';
        oscillator.frequency.value = event.config.frequency;
        const gainNode = this.context.createGain();
        gainNode.gain.value = (_b = event.config.gain) !== null && _b !== void 0 ? _b : 0.1;
        oscillator.connect(gainNode).connect(this.gain);
        oscillator.start();
        this.sustainNodes.set(name, { oscillator, gain: gainNode });
    }
    stopSustain(name) {
        const node = this.sustainNodes.get(name);
        if (!node || !this.context)
            return;
        node.gain.gain.exponentialRampToValueAtTime(0.0001, this.context.currentTime + 0.15);
        node.oscillator.stop(this.context.currentTime + 0.2);
        this.sustainNodes.delete(name);
    }
    stopAll() {
        for (const key of Array.from(this.sustainNodes.keys())) {
            this.stopSustain(key);
        }
    }
    ensureContext() {
        if (this.context)
            return;
        if (typeof window === 'undefined' || typeof AudioContext === 'undefined')
            return;
        this.context = new AudioContext();
        this.gain = this.context.createGain();
        this.gain.gain.value = 0.4;
        this.gain.connect(this.context.destination);
    }
}
